import { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { refreshAdmin } from '../../lib/admin-auth-api';
import { useAdminAuthStore } from '../../stores/useAdminAuthStore';

let adminBootstrapPromise: Promise<boolean> | null = null;

function ensureAdminBootstrap(): Promise<boolean> {
  if (adminBootstrapPromise) {
    return adminBootstrapPromise;
  }

  const state = useAdminAuthStore.getState();
  state.setBootstrapping(true);

  adminBootstrapPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return true;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return false;
    })
    .finally(() => {
      useAdminAuthStore.getState().setBootstrapping(false);
      adminBootstrapPromise = null;
    });

  return adminBootstrapPromise;
}

export default function AdminRouteGuard() {
  const navigate = useNavigate();
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const user = useAdminAuthStore((state) => state.user);
  const isBootstrapping = useAdminAuthStore((state) => state.isBootstrapping);
  const suppressBootstrapUntilLogin = useAdminAuthStore(
    (state) => state.suppressBootstrapUntilLogin,
  );

  useEffect(() => {
    if (accessToken && user) {
      return;
    }

    if (suppressBootstrapUntilLogin) {
      return;
    }

    let isMounted = true;

    void ensureAdminBootstrap().then((didRestoreSession) => {
      if (!isMounted || didRestoreSession) {
        return;
      }

      navigate('/admin/login', { replace: true });
    });

    return () => {
      isMounted = false;
    };
  }, [accessToken, navigate, suppressBootstrapUntilLogin, user]);

  if (accessToken && user) {
    return <Outlet />;
  }

  if (suppressBootstrapUntilLogin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-6 text-slate-100">
      <div
        aria-live="polite"
        className="rounded-xl border border-slate-800 bg-slate-900 px-5 py-4 text-sm shadow-lg"
      >
        {isBootstrapping ? 'Loading admin session...' : 'Preparing admin session...'}
      </div>
    </div>
  );
}
